<template>
  <div class="mnemonic-card">
    <div class="mnemonic-input" @click="focusInput">
      <template v-for="(item, index) in words" :key="`word-${index}`">
        <span
          v-if="editingIndex !== index"
          class="word-tag"
          :class="item.valid ? 'word-valid' : 'word-invalid'"
          role="button"
          tabindex="0"
          @mousedown.prevent="startEditWord(index)"
        >
          <span class="word-index">{{ index + 1 }}.</span>
          <span class="word-text">{{ item.word }}</span>
        </span>
        <div
          v-else
          class="word-tag word-edit-wrap"
          :class="item.valid ? 'word-valid' : 'word-invalid'"
        >
          <span class="word-index">{{ index + 1 }}.</span>
          <input
            ref="editInput"
            v-model="currentInput"
            class="word-edit-input"
            type="text"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            @keydown="handleKeydown"
            @input="handleTyping"
            @blur="commitEditWord"
          />
        </div>
      </template>

      <input
        v-if="editingIndex < 0 && words.length < maxWords"
        ref="hiddenInput"
        v-model="currentInput"
        class="hidden-input"
        :class="{ 'full-input': !words.length }"
        type="text"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        :placeholder="words.length ? '' : placeholder"
        @keydown="handleKeydown"
        @input="handleTyping"
      />
    </div>
  </div>
</template>

<script>
import { validateWord, normalizeMnemonic, joinMnemonicParts } from '../utils/mnemonicValidator';

/**
 * 助记词「编号格子」输入控件（小狐狸 / OKX 同款格式）：
 * 每个词一个带序号的方框，最后一格是当前输入框；点击方框可单独修改该词。
 * v-model 输出空格分隔的助记词字符串，父组件直接丢给 validateMnemonic 校验。
 */
export default {
  name: 'MnemonicWordInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    /** 允许的最大词数：只接受 12 个词的钱包传 12，支持 12/15/18/21/24 的传 24 */
    maxWords: {
      type: Number,
      default: 12
    },
    placeholder: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      words: [],
      currentInput: '',
      editingIndex: -1
    };
  },
  computed: {
    /** 已提交的词 + 正在输入的词尾，即当前完整助记词 */
    phrase() {
      const parts = this.words.map((item, index) => {
        if (index === this.editingIndex && this.currentInput.trim()) {
          return this.currentInput.trim().toLowerCase();
        }
        return item.word;
      });
      const tail = this.editingIndex < 0 ? this.currentInput : '';
      return joinMnemonicParts(parts.join(' '), tail);
    },
    hasContent() {
      return this.words.length > 0 || this.currentInput.trim().length > 0;
    }
  },
  watch: {
    phrase: {
      immediate: true,
      handler(value) {
        if (value !== this.modelValue) {
          this.$emit('update:modelValue', value);
        }
      }
    }
  },
  methods: {
    focusInput() {
      if (this.editingIndex >= 0) return;
      if (this.$refs.hiddenInput) this.$refs.hiddenInput.focus();
    },
    focusEditInput() {
      this.$nextTick(() => {
        const el = this.$refs.editInput;
        const input = Array.isArray(el) ? el[0] : el;
        if (!input) return;
        input.focus();
        input.select();
      });
    },
    startEditWord(index) {
      if (index < 0 || index >= this.words.length) return;
      if (this.editingIndex === index) {
        this.focusEditInput();
        return;
      }
      if (this.editingIndex >= 0) {
        this.commitEditWord();
        if (index >= this.words.length) return;
      }
      this.editingIndex = index;
      this.currentInput = this.words[index].word;
      this.focusEditInput();
    },
    commitEditWord() {
      if (this.editingIndex < 0) return;
      const index = this.editingIndex;
      const token = this.createWordToken(this.currentInput);
      if (token) {
        this.words[index] = token;
      } else if (this.words.length > 0) {
        this.words.splice(index, 1);
      }
      this.editingIndex = -1;
      this.currentInput = '';
    },
    createWordToken(rawWord) {
      const cleaned = normalizeMnemonic(rawWord).split(' ')[0] || '';
      if (!cleaned) return null;
      return { word: cleaned, valid: validateWord(cleaned).valid };
    },
    appendWordsFromText(text) {
      const chunks = String(text || '').replace(/\r\n?/g, '\n').split(/\s+/).filter(Boolean);
      if (!chunks.length) return;
      chunks.forEach(chunk => {
        if (this.words.length >= this.maxWords) return;
        const token = this.createWordToken(chunk);
        if (token) this.words.push(token);
      });
    },
    commitCurrentWord() {
      if (this.words.length >= this.maxWords) {
        this.currentInput = '';
        return;
      }
      const token = this.createWordToken(this.currentInput);
      if (!token) {
        this.currentInput = '';
        return;
      }
      this.words.push(token);
      this.currentInput = '';
    },
    handleKeydown(event) {
      const isEditing = this.editingIndex >= 0;

      if (!isEditing && this.words.length >= this.maxWords && !this.currentInput) {
        event.preventDefault();
        return;
      }

      if (event.key === ' ' || event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (isEditing) {
          this.commitEditWord();
          if (event.key !== 'Tab') this.focusInput();
        } else {
          this.commitCurrentWord();
        }
      } else if (event.key === 'Escape' && isEditing) {
        event.preventDefault();
        this.editingIndex = -1;
        this.currentInput = '';
      } else if (event.key === 'Backspace' && !this.currentInput) {
        if (isEditing) {
          const index = this.editingIndex;
          this.words.splice(index, 1);
          this.editingIndex = -1;
          if (index > 0) {
            const prev = this.words[index - 1];
            this.currentInput = prev ? prev.word : '';
            this.words.splice(index - 1, 1);
            this.editingIndex = index - 1;
            this.focusEditInput();
          }
        } else if (this.words.length > 0) {
          const last = this.words.pop();
          this.currentInput = last ? last.word : '';
        }
      }
    },
    handleTyping() {
      const isEditing = this.editingIndex >= 0;
      if (!isEditing && this.words.length >= this.maxWords) {
        this.currentInput = '';
        return;
      }
      if (!this.currentInput) return;
      if (/\s/.test(this.currentInput)) {
        const segments = this.currentInput.split(/\s+/);
        const tail = (segments.pop() || '').toLowerCase();
        const complete = segments.filter(Boolean).join(' ');
        if (complete) this.appendWordsFromText(complete);
        this.currentInput = tail;
      } else {
        this.currentInput = this.currentInput.toLowerCase();
      }
    },
    clearAll() {
      this.words = [];
      this.currentInput = '';
      this.editingIndex = -1;
      this.$nextTick(() => this.focusInput());
    },
    /** 读取剪贴板：标准 API → Android 原生桥 → execCommand 兜底 */
    async readClipboardText() {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.readText === 'function'
      ) {
        try {
          const text = await navigator.clipboard.readText();
          if (text) return text;
        } catch (error) {
          console.warn('navigator.clipboard.readText failed, fallback to AppBridge:', error);
        }
      }

      const bridge = typeof window !== 'undefined' ? window.AppBridge : null;
      if (bridge && typeof bridge.readClipboard === 'function') {
        try {
          const text = await bridge.readClipboard();
          if (typeof text === 'string' && text) return text;
        } catch (error) {
          console.warn('AppBridge.readClipboard failed:', error);
        }
      }

      return this.readClipboardViaExecCommand();
    },
    readClipboardViaExecCommand() {
      if (typeof document === 'undefined' || !document.execCommand) return '';
      const helper = document.createElement('textarea');
      helper.setAttribute('aria-hidden', 'true');
      helper.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0;';
      document.body.appendChild(helper);
      let result = '';
      try {
        helper.focus();
        helper.select();
        if (document.execCommand('paste')) {
          result = helper.value || '';
        }
      } catch (_) {
        /* ignore */
      } finally {
        helper.remove();
      }
      return result;
    },
    /** 整段粘贴：覆盖当前内容，按空格/换行拆成编号格子 */
    async paste() {
      const text = await this.readClipboardText();
      const cleaned = String(text || '').trim();
      this.words = [];
      this.currentInput = '';
      this.editingIndex = -1;
      if (!cleaned) return false;
      this.appendWordsFromText(cleaned);
      this.$nextTick(() => this.focusInput());
      return this.words.length > 0;
    }
  }
};
</script>

<style scoped>
.mnemonic-card {
  --cell-height: 56px;
  --cell-gap: 10px;
  width: 100%;
  min-height: calc(36px + var(--cell-height) * 4 + var(--cell-gap) * 3);
  border-radius: 12px;
  background: #ebecef;
  padding: 16px;
  box-sizing: border-box;
}

.mnemonic-input {
  width: 100%;
  border: none;
  background: transparent;
  color: #4e5460;
  font-size: 16px;
  line-height: 1.6;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: var(--cell-height);
  gap: var(--cell-gap);
  cursor: text;
}

.hidden-input {
  width: 100%;
  min-height: var(--cell-height);
  border: 1.5px solid #4b63ff;
  border-radius: 10px;
  outline: none;
  background: #f4f5f7;
  color: #131722;
  font-size: clamp(14px, 4vw, 16px);
  line-height: 1.2;
  padding: 0 12px;
  box-sizing: border-box;
}

.hidden-input.full-input {
  grid-column: 1 / -1;
  border: none;
  border-radius: 0;
  background: transparent;
  color: #4e5460;
  line-height: 1.6;
  padding: 0;
}

.hidden-input::placeholder {
  color: #6a7080;
  font-size: clamp(14px, 4vw, 16px);
}

.word-tag {
  display: inline-flex;
  align-items: center;
  width: 100%;
  min-height: var(--cell-height);
  padding: 6px 8px;
  border-radius: 10px;
  font-size: clamp(13px, 3.7vw, 16px);
  line-height: 1.2;
  background: #f4f5f7;
  color: #131722;
  border: 1.5px solid #aeb4c3;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  cursor: pointer;
}

.word-edit-wrap {
  cursor: text;
  padding: 0;
  border-color: #4b63ff;
}

.word-edit-wrap .word-index {
  padding-left: 8px;
  flex-shrink: 0;
}

.word-edit-input {
  flex: 1;
  min-width: 0;
  min-height: calc(var(--cell-height) - 2px);
  border: none;
  outline: none;
  background: transparent;
  color: #131722;
  font-size: clamp(13px, 3.7vw, 16px);
  line-height: 1.2;
  padding: 0 8px 0 0;
  box-sizing: border-box;
}

.word-valid {
  border-color: #aeb4c3;
}

.word-invalid {
  border-color: #d84a5a;
}

.word-index {
  color: #6c7380;
  margin-right: 5px;
  font-weight: 600;
  flex-shrink: 0;
}

.word-text {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
